import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Async retry helper for transient Google API errors (5XX server errors).
 * @param fn The async function to retry
 * @param retries Number of retries
 * @param delay Initial delay in ms
 * @param backoff Backoff multiplier
 */
export async function retryGoogleApi<T>(fn: () => Promise<T>, retries = 3, delay = 1000, backoff = 2): Promise<T> {
  try {
    return await fn();
  } catch (err: any) {
    // Check for Google API 5XX server errors (transient errors)
    const status = err?.response?.status || err?.code || err?.statusCode;
    const statusNum = typeof status === 'string' ? parseInt(status, 10) : status;
    if (retries > 0 && statusNum >= 500 && statusNum < 600) {
      console.warn(`⚠️  Retrying after Google API server error (status: ${status}), in ${delay}ms... (${retries} retries left)`);
      await new Promise(res => setTimeout(res, delay));
      return retryGoogleApi(fn, retries - 1, delay * backoff, backoff);
    }
    throw err;
  }
}